import { memo } from "react";

import { Box, Stack } from "@mui/material";

import ChatMessage from "../ChatMessage/ChatMessage";
import { ChatMessageWithParts } from "src/views/assistant/types";
import PreferredResponseText from "./PreferredResponseText";
import useMessageGroups from "../hooks/useMessageGroups";

type ChatMessageGroupProps = {
  "data-testid"?: string;
  dialogue: ChatMessageWithParts[];
  messageGroup: ChatMessageWithParts[];
  messageGroupIdx: number;
  isChatConnected: boolean;
  isTurnActive?: boolean;
  isChatMessageStreamStopped: boolean;
  resendUserMessage: (id: string, text: string) => void;
  regenerateResponse: (id: string) => void;
  setAsPreferredMessage: (id: string) => void;
};

const noop = () => {};

const ChatMessageGroup = memo(
  ({
    "data-testid": dataTestId,
    dialogue,
    messageGroup,
    messageGroupIdx,
    isChatConnected,
    isTurnActive = false,
    isChatMessageStreamStopped,
    resendUserMessage,
    regenerateResponse,
    setAsPreferredMessage,
  }: ChatMessageGroupProps) => {
    const {
      sortedMessageGroup,
      currentIndex,
      isMultipleMessages,
      hasPreferredMessage,
      groupContainsDpo,
      isAssistantGroup,
      isPreferredMessagePicked,
      handlePrev,
      handleNext,
    } = useMessageGroups(messageGroup);

    const isRenderPreferredResponseText =
      groupContainsDpo &&
      !isPreferredMessagePicked &&
      isAssistantGroup &&
      isChatMessageStreamStopped;

    return (
      <Box
        data-testid={dataTestId}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        {isRenderPreferredResponseText && (
          <Box sx={{ alignSelf: "center", textAlign: "center", mt: 1 }}>
            <PreferredResponseText />
          </Box>
        )}

        <Stack direction="row" gap={1}>
          {isMultipleMessages && hasPreferredMessage && (
            <Box
              sx={{ width: "100%", display: "flex", flexDirection: "column" }}
            >
              <ChatMessage
                key={sortedMessageGroup[currentIndex].id}
                dialogue={dialogue}
                chatMessage={sortedMessageGroup[currentIndex]}
                sortedMessageGroup={sortedMessageGroup}
                messageGroupIdx={messageGroupIdx}
                messageIndex={messageGroupIdx}
                currentIndex={currentIndex}
                isChatConnected={isChatConnected}
                isTurnActive={isTurnActive}
                isChatMessageStreamStopped={isChatMessageStreamStopped}
                isPreferredMessagePicked={isPreferredMessagePicked}
                resendUserMessage={resendUserMessage}
                regenerateResponse={regenerateResponse}
                setAsPreferredMessage={setAsPreferredMessage}
                handlePrev={handlePrev}
                handleNext={handleNext}
              />
            </Box>
          )}

          {!hasPreferredMessage &&
            sortedMessageGroup?.map((message, index) => (
              <ChatMessage
                key={message.id + "-" + index}
                data-testid={`aa-msg-${messageGroupIdx}-${message.id}`}
                dialogue={dialogue}
                chatMessage={message}
                sortedMessageGroup={sortedMessageGroup}
                messageGroupIdx={messageGroupIdx}
                messageIndex={messageGroupIdx}
                isChatConnected={isChatConnected}
                isTurnActive={isTurnActive}
                isChatMessageStreamStopped={isChatMessageStreamStopped}
                resendUserMessage={resendUserMessage}
                regenerateResponse={regenerateResponse}
                setAsPreferredMessage={setAsPreferredMessage}
                handlePrev={noop}
                handleNext={noop}
              />
            ))}
        </Stack>
      </Box>
    );
  },
);

export default ChatMessageGroup;
